import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signupRequest, generateOtp, varifyOtp, clearMessage } from '../../actions/userActions';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa';
import './Signup.css';
import GoogleLoginButton from './GoogleLoginButton';

function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const notificationTimerRef = useRef(null);
  const resendTimerRef = useRef(null);

  // State management
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isStudentSignup, setStudentSignup] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobileNumber: '',
    role: "Student"
  });

  // Redux selectors
  const signupSuccessMessage = useSelector(state => state.user.SignupSucess);
  const signupFailure = useSelector(state => state.user.SignupFailure);
  const otpGenerated = useSelector(state => state.user.otpGenerated);
  const verifiedOtp = useSelector(state => state.user.varifiedOtp);
  const otpGeneratedError = useSelector(state => state.user.otpGeneratedError);
  const verifiedOTPError = useSelector(state => state.user.varifiedOTPError);
  const message = useSelector(state => state.user.message);
  const otpSuccess = useSelector(state => state.user.otpSuccess);
  const isLoading = useSelector(state => state.user.loading);

  // Clear notifications
  const clearNotification = () => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification({ show: false, type: '', message: '' });
  };

  // Show custom notification
  const showCustomNotification = (type, message) => {
    clearNotification();
    setNotification({ show: true, type, message });
    notificationTimerRef.current = setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Reset all states
  const resetAllStates = () => {
    setOtp(['', '', '', '']);
    setFormData({
      name: '',
      email: '',
      password: '',
      mobileNumber: '',
      role: "Student"
    });
    setEmailVerified(false);
    setEmailDisabled(false);
    setIsButtonDisabled(true);
    setIsEmailValid(false);
    setErrors({});
    setOtpResendTimer(0);
    setIsVerifying(false);
    setIsSubmitting(false);
    clearNotification();
    dispatch(clearMessage());
  };

  // Start OTP resend timer
  const startResendTimer = () => {
    setOtpResendTimer(120);
    resendTimerRef.current = setInterval(() => {
      setOtpResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handlers
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });

    if (name === "email") {
      setIsEmailValid(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value));
    }
  };

  const handleOtpChange = (index, value) => {
    if (/[^0-9]/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleUserType = (isStudent) => {
    setStudentSignup(isStudent);
    setFormData({ ...formData, role: isStudent ? "Student" : "Teacher" });
  };

  const handleSendOTP = () => {
    if (!isEmailValid) {
      showCustomNotification('warning', 'Please enter a valid email address');
      return;
    }

    if (otpResendTimer > 0) return;

    dispatch(generateOtp({
      email: formData.email,
      isStudent: isStudentSignup,
      newuser: true
    }));
    startResendTimer();
  };

  const handleVerifyOtp = () => {
    if (isButtonDisabled) return;
    setIsVerifying(true);
    dispatch(varifyOtp({
      email: formData.email,
      otp: otp.join("")
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isEmailValid) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number is invalid';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!emailDisabled) {
      showCustomNotification('warning', 'Please verify your email first');
      return;
    }

    if (validateForm()) {
      setIsSubmitting(true);
      dispatch(signupRequest(formData));
    }
  };


  // Effects
  useEffect(() => {
    return () => {
      clearNotification();
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
      dispatch(clearMessage());
    };
  }, [dispatch]);

  useEffect(() => {
    setIsButtonDisabled(!otp.every(value => value !== ''));
  }, [otp]);

  useEffect(() => {
    if (otpGenerated) {
      if (otpSuccess === "key generated") {
        showCustomNotification('success', 'OTP has been sent to your email');
        setEmailVerified(true);
      } else if (otpSuccess === "User Already Registered") {
        showCustomNotification('error', 'Please login or use another email');
        setOtpResendTimer(0);
        clearInterval(resendTimerRef.current);
      }
    } else if (otpGeneratedError) {
      showCustomNotification('error', 'Failed to send OTP. Please try again.');
      setOtpResendTimer(0);
      clearInterval(resendTimerRef.current);
    }
    dispatch(clearMessage());
  }, [otpGenerated, otpGeneratedError, otpSuccess, dispatch]);
  useEffect(() => {
    if (verifiedOtp) {
      setIsVerifying(false);
      if (message === "otp varified") {
        showCustomNotification('success', 'Your email has been successfully verified');
        setEmailDisabled(true);
      } else if (message === "otp not varified") {
        showCustomNotification('error', 'The OTP you entered is incorrect');
      }
    } else if (verifiedOTPError) {
      setIsVerifying(false);
      showCustomNotification('error', 'Something went wrong. Please try again.');
    }
    dispatch(clearMessage());
  }, [verifiedOtp, verifiedOTPError, message, dispatch]);
  useEffect(() => {
    if (signupSuccessMessage) {
      showCustomNotification('success', 'Account created successfully! Redirecting to login...');
      const timer = setTimeout(() => {
        setIsSubmitting(false);
        resetAllStates();
        navigate('/signin');
      }, 2000);
      return () => clearTimeout(timer);
    } else if (signupFailure) {
      setIsSubmitting(false);
      showCustomNotification('error', 'Failed to create account. Please try again.');
    }
    dispatch(clearMessage());
  }, [signupSuccessMessage, signupFailure, navigate, dispatch]); 

  return (
    <div className="signup-container">
      {notification.show && (
        <div className={`custom-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="signup-box">
        <div className="signup-header">
          <h2>{isStudentSignup ? 'Student Sign Up' : 'Creator Sign Up'}</h2>
          <p>Join our community of learners and educators</p>
        </div>

        <div className="user-type-toggle">
          <button
            className={`toggle-btn ${isStudentSignup ? 'active' : ''}`}
            onClick={() => toggleUserType(true)}
            type="button"
          >
            Student
          </button>
          <button
            className={`toggle-btn ${!isStudentSignup ? 'active' : ''}`}
            onClick={() => toggleUserType(false)}
            type="button"
          >
            Creator
          </button>
        </div>


        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Full Name"
              className={errors.name ? 'error-input' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group email-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              className={errors.email ? 'error-input' : ''}
              disabled={emailDisabled}
            />
            <button
              type="button"
              className="otp-btn"
              onClick={handleSendOTP}
              disabled={emailDisabled || !isEmailValid || otpResendTimer > 0 || isLoading}
            >
              {otpResendTimer > 0 ? (
                `${otpResendTimer}s`
              ) : isLoading ? (
                <FaSpinner className="spin" />
              ) : otpGenerated ? (
                "Sent"
              ) : (
                "Send OTP"
              )}
            </button>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {emailVerified && (
            <div className="form-group otp-group">
              <div className="otp-inputs">
                {otp.map((value, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    value={value}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    maxLength="1"
                    className="otp-input"
                  />
                ))}
              </div>
              <button
                type="button"
                className="verify-btn"
                onClick={handleVerifyOtp}
                disabled={isButtonDisabled || emailDisabled || isVerifying}
              >
                {emailDisabled ? (
                  "Verified"
                ) : isVerifying ? (
                  <>
                    <FaSpinner className="spin" /> Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          )}

          <div className="form-group">
            <input
              type="text"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              placeholder="Mobile Number"
              className={errors.mobileNumber ? 'error-input' : ''}
            />
            {errors.mobileNumber && <span className="error-message">{errors.mobileNumber}</span>}
          </div>

          <div className="form-group password-group">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className={errors.password ? 'error-input' : ''}
            />
            <span
              className="password-toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={!emailDisabled || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spin" /> Creating Account...
              </>
            ) : (
              "Sign Up"
            )}
          </button>
        </form>
        {/* Google Sign Up Button */}
        <div className="social-signup-section">
          <div className="divider">
            <span>Sign up with Google</span>
          </div>
          <GoogleLoginButton
            userType={isStudentSignup ? 'student' : 'creator'}
            disabled={isSubmitting}
          />
        </div>

        <div className="login-link">
          Already have an account? <Link to="/signin">Log In</Link>
        </div>
      </div>
    </div>
  );
}

export default SignUp;